import { getExperimentsAttributes } from '#pplx/frontend/apps/ask/queries/experiment-queries/getExperimentsAttributes.js';
import { getExperimentsAttributesQueryKey } from '#pplx/frontend/apps/ask/queries/experiment-queries/getExperimentsAttributesQueryKey.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useMemo } from 'react';
/**
 * Custom hook to check if the current user is Existing Comet User.
 *
 * ExistingComet User Definition:
 * User has made a logged in query on Comet,
 * or has downloaded Comet when logged in ('click download comet' with non-null user_id).
 *
 *
 * Reference pplx/backend/web/scripts/emails/comet_users_query.py for SQL query
 * for cron script in pplx/python/apps/assign_to_cohorts_cronjob that adds Existing Comet Users to COMET_USER_COHORT
 *
 * This hook fetches experiment attributes with a 5-minute cache to reduce unnecessary API calls.
 *
 * @returns Object containing:
 * - isExistingCometUser: boolean | undefined - true if user is a Comet user, false if not, undefined if loading/error
 * - isLoading: boolean - true while fetching data
 * - error: Error | null - any error that occurred during fetching
 *
 */
export const useIsExistingCometUser = () => {
    const isLoggedIn = useIsUserLoggedIn();
    const { data: attributes, isLoading, error, } = useQuery({
        queryKey: getExperimentsAttributesQueryKey(),
        queryFn: () => getExperimentsAttributes({
            reason: 'check if user is an existing comet user',
        }),
        // Consider data fresh for 5 minutes - reduces unnecessary API calls
        staleTime: 5 * 60 * 1000,
        // Keep in cache for 5 minutes after component unmount
        gcTime: 5 * 60 * 1000,
        // Only fetch if user is logged in
        enabled: isLoggedIn,
        // Don't retry on failure to avoid spamming the API
        retry: false,
    });
    const isExistingCometUser = useMemo(() => {
        if (!attributes)
            return undefined;
        // server_is_comet_eligible indicates if user is eligible for Comet features
        // true = user is not an Existing Comet user
        // false = user is an Existing Comet user
        // null/undefined = unknown status
        return attributes.server_is_comet_eligible === false;
    }, [attributes]);
    return {
        isExistingCometUser,
        isLoading,
        error,
    };
};
//# sourceMappingURL=useIsExistingCometUser.js.map